'use client';

import React from 'react';
import { useTranslation } from '@/lib/i18n';
import { XIcon, Loader2 } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import dayjs from 'dayjs';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isCanceling: boolean;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isCanceling,
}) => {
  const { t } = useTranslation();
  const { currentPlanExp, currentPlanInfo } = useSubscription();

  if (!isOpen) return null;

  const planName = currentPlanInfo?.planType
    ? currentPlanInfo.planType.charAt(0).toUpperCase() + currentPlanInfo.planType.slice(1)
    : '';
  const expDate = currentPlanExp ? dayjs(currentPlanExp).format('MMM D, YYYY') : '';

  // 不把 date 传入 t() 插值，保留 {{date}} 占位符原样，以便拆开单独给日期上色
  const [bodyBeforeDate, bodyAfterDate] = t(
    "You can use {{plan}} until {{date}}. After that, your plan will switch to Free with no further charges. Your earned Credits won't be affected.",
    { plan: planName },
  ).split('{{date}}');

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-100" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-101 pointer-events-none">
        <div className="relative w-[480px] max-w-[calc(100vw-32px)] bg-[#1B1B1B] rounded-2xl border border-[#2F2F2F] pt-12 pb-8 px-10 pointer-events-auto">
          {/* Close */}
          <button
            onClick={onClose}
            aria-label={t('Close')}
            className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <XIcon size={18} />
          </button>

          {/* Icon */}
          <div className="flex justify-center">
            <img
              src="/image/subscription/cancel-plan-icon.png"
              alt=""
              aria-hidden="true"
              className="w-20 h-20 select-none"
            />
          </div>

          {/* Content */}
          <div className="mt-8 flex flex-col gap-3 text-center">
            <h2 className="text-xl text-white">
              {t('Cancel {{plan}} Subscription?', { plan: planName })}
            </h2>
            <p className="text-sm text-[#B7B9BC] leading-relaxed">
              {bodyBeforeDate}
              <span className="text-[#A772FF] font-bold">{expDate}</span>
              {bodyAfterDate}
            </p>
          </div>

          {/* Buttons */}
          <div className="mt-8 flex items-center justify-center gap-5">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-full text-[16px] font-semibold text-white bg-[#8B3FFC] hover:opacity-90 transition-opacity cursor-pointer"
            >
              {t('Keep my plan')}
            </button>
            <button
              onClick={onConfirm}
              disabled={isCanceling}
              className="flex-1 flex items-center justify-center py-3 rounded-full text-[16px] font-medium text-[#B7B9BC] bg-[#2C2C30] hover:opacity-90 transition-opacity disabled:opacity-60 cursor-pointer"
            >
              {isCanceling && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('Cancel Subscription')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CancelSubscriptionModal;
